import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Sparkles, Target, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ResumeUploader } from '@/components/resume/ResumeUploader';
import { Helmet } from 'react-helmet-async';

type CreationMode = null | 'upload' | 'scratch' | 'tailor';

export default function ResumeCreationHub() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<CreationMode>(null);

  if (mode === 'upload') {
    return (
      <DashboardLayout>
        <Helmet>
          <title>Upload & Optimize Resume | LoopBaan.AI</title>
          <meta name="description" content="Upload your existing resume and let AI optimize it for ATS compatibility" />
        </Helmet>

        <div className="max-w-3xl mx-auto space-y-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMode(null)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to options
          </Button>

          <div>
            <h1 className="text-3xl font-bold mb-2">Upload & Optimize Your Resume</h1>
            <p className="text-muted-foreground">
              Upload your existing resume and let our AI enhance it for better ATS scores and more interviews
            </p>
          </div>

          <ResumeUploader
            onUploadComplete={() => {
              // Navigate to resume library after upload
              navigate('/app/resume/library');
            }}
          />
        </div>
      </DashboardLayout>
    );
  }

  if (mode === 'scratch') {
    // Redirect to existing resume builder
    navigate('/resume');
    return null;
  }

  if (mode === 'tailor') {
    // Navigate to tailoring flow
    navigate('/app/resume/tailor-to-job');
    return null;
  }

  return (
    <DashboardLayout>
      <Helmet>
        <title>Create Your Resume | LoopBaan.AI</title>
        <meta name="description" content="Choose how to create your professional, ATS-optimized resume" />
      </Helmet>

      <div className="max-w-5xl mx-auto space-y-8">
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold">Create Your Resume</h1>
          <p className="text-xl text-muted-foreground">
            Choose how you'd like to get started
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Upload & Optimize */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50" onClick={() => setMode('upload')}>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Upload className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">Upload & Optimize</CardTitle>
              </div>
              <CardDescription className="text-base">
                Have a resume? Upload it and let our AI enhance it for better results
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>ATS optimization & keyword enhancement</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Better formatting & achievement focus</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Side-by-side comparison with improvements</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Instant ATS score before & after</span>
                </li>
              </ul>
              <Button className="w-full mt-4" onClick={() => setMode('upload')}>
                Upload Resume
              </Button>
            </CardContent>
          </Card>

          {/* Create From Scratch */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50" onClick={() => setMode('scratch')}>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-2xl">Create From Scratch</CardTitle>
              </div>
              <CardDescription className="text-base">
                No resume yet? We'll build one from your profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Professional templates for any industry</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>AI-generated content with target keywords</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Tailored to your experience level</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>ATS-friendly from the start</span>
                </li>
              </ul>
              <Button className="w-full mt-4" onClick={() => setMode('scratch')}>
                Start Creating
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-4">
          <Separator className="flex-1" />
          <span className="text-sm text-muted-foreground font-medium">OR</span>
          <Separator className="flex-1" />
        </div>

        {/* Tailor to Job Posting */}
        <Card className="hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-primary/50" onClick={() => setMode('tailor')}>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 rounded-lg bg-primary/10">
                <Target className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Tailor to Job Posting</CardTitle>
                <CardDescription className="text-base mt-1">
                  Paste a job description and we'll create a resume specifically optimized for that role
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="space-y-2 text-sm text-muted-foreground">
                <p className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Maximum keyword match for specific job</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Rewritten bullets to match requirements</span>
                </p>
                <p className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">✓</span>
                  <span>Highest chance of getting past ATS</span>
                </p>
              </div>
              <Button size="lg" onClick={() => setMode('tailor')}>
                Tailor Resume to Job
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center text-sm text-muted-foreground">
          <p>All resume creation and optimization features use AI credits</p>
          <p className="mt-1">Upload & parse is free • Optimization costs 1-2 credits</p>
        </div>
      </div>
    </DashboardLayout>
  );
}
